import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { StatefulButton } from '@edx/paragon';
import Lottie from 'lottie-react';
import PropTypes from 'prop-types';

import { checkCodeStatus, sendRegistrationCode, verifyRegistrationCode } from '../../registration-code/data/service';
import { EmailLoaderLottie } from '../../assets/lottie';
import ArrowButton from '../../common-components/ArrowButton';
import EmailRetryIssueNotice from '../../common-components/EmailRetryIssueNotice';
import VerificationCodeInput from '../../common-components/VerificationCodeInput';
import { MAXIMUM_FAILED_ATTEMPS_RESEND_EMAIL } from '../../data/constants';
import { OTP_LENGTH } from '../../forgot-password-code/data/constants';
import messages from '../messages';

const EmailVerification = ({
  formFields,
  handleGoToNextStep,
  handleGoToPrevStep,
  initialToken,
}) => {
  const [failedAttempts, setFailedAttempts] = useState(0);
  const verificationCodeInputRef = useRef();
  const [errorMessage, setErrorMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [token, setToken] = useState('');
  const [remainingSeconds, setRemainingSeconds] = useState(-1);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const [showSendAgain, setShowSendAgain] = useState(false);

  const { formatMessage } = useIntl();

  const {
    thirdPartyAuthContext: { currentProvider },
  } = useSelector((state) => state.commonComponents);

  const hasProvider = Boolean(currentProvider);

  // Auto-advance for third-party auth users
  useEffect(() => { 
    if (hasProvider) {
      handleGoToNextStep();
    }
  }, [hasProvider, handleGoToNextStep]);

  // Shared function to send/resend registration code
  const sendOrResendCode = async (isResend = false) => {
    if (isResend) {
      setResendLoading(true);
    }
    
    try {
      const response = await sendRegistrationCode(formFields.email);

      if (response.token) {
        setToken(response.token);
      }
      
      if (isResend) {
        setFailedAttempts(prev => prev + 1);
        setShowSendAgain(false);
        setIsCodeInvalid(false);
        setErrorMessage('');
        if (verificationCodeInputRef.current) {
          verificationCodeInputRef.current.reset();
        }
      }
    } catch (err) {
      console.error(`Failed to ${isResend ? 'resend' : 'send'} registration code:`, err);
    } finally {
      if (isResend) {
        setResendLoading(false);
      }
    }
  };

  // regular registration flow (skip if backend already sent via initialToken)
  useEffect(() => {
    if (!hasProvider && !initialToken && formFields.email) {
      sendOrResendCode(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // inactive user generate token to activate account
  useEffect(() => {
    if (initialToken) {
      setToken(initialToken);
    }
  }, [initialToken]);

  const handleResendCode = () => {
    sendOrResendCode(true);
  };

  const handleEnterCode = () => {
    setShowCodeInput(true);
  };

  const handleVerificationChange = (newCode) => {
    setVerificationCode(newCode);
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length < OTP_LENGTH || !token) {
      return;
    }

    setIsVerifying(true);
    setIsCodeInvalid(false);
    setErrorMessage('');

    try {
      const response = await verifyRegistrationCode(token, verificationCode);
      if (response.is_valid) {
        // Success - proceed to the next step in the registration flow
        handleGoToNextStep();
      } else {
        setIsCodeInvalid(true);
        setErrorMessage(formatMessage(messages['verification.code.input.invalid.value']));
      }
    } catch (err) {
      setIsCodeInvalid(true);
      setErrorMessage(formatMessage(messages['verification.code.input.invalid.value']) || err.response?.data?.error);
    } finally {
      setIsVerifying(false);
    }
  };

  // Fetch timer when entering code input
  useEffect(() => {
    if (showCodeInput && token) {
      const fetchTimer = async () => {
        try {
          const response = await checkCodeStatus(token);
          if (response.expired || response.remaining_seconds <= 0) {
            setRemainingSeconds(0);
            setShowSendAgain(true);
          } else {
            setRemainingSeconds(response.remaining_seconds);
          }
        } catch (err) {
          console.error('Failed to check code status:', err);
        }
      };
      fetchTimer();
    }
  }, [showCodeInput, token]);

  // Countdown timer
  useEffect(() => {
    if (!showCodeInput || remainingSeconds <= 0) {
      return;
    }

    const timerId = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          setShowSendAgain(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [showCodeInput, remainingSeconds]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const remainSeconds = time % 60;
    return `${minutes}:${remainSeconds < 10 ? `0${remainSeconds}` : remainSeconds}`;
  };

  //Component doesn't render for third-party auth users
  if (hasProvider) { 
    return null;
  }

  return (
    <div className="flex-center-column flex-fill email-verification" data-gtm="email-verification">
      <div className="h-70 d-flex justify-content-between flex-center-column">
        <div className="flex-center-column mb-6">
          <h1 className="page-title">{formatMessage(messages['email.verification.page.title'])}</h1>
          <p className="regular-text text-center mb-4 mt-4">
            {formatMessage(messages['email.verification.page.subtitle'], {
              email: (
                <span style={{ fontWeight: 'bold' }}>{formFields.email}</span>
              ),
              platform_name: getConfig().SITE_NAME,
              enter: <br />,
            })}
          </p>

          {showCodeInput ? (
            <>
              <VerificationCodeInput
                ref={verificationCodeInputRef}
                length={OTP_LENGTH}
                isInvalid={isCodeInvalid ||(showCodeInput && remainingSeconds === 0 )}
                isSuccess={false}
                onComplete={handleVerificationChange}
              />
              <div className="w-100 mt-3 d-flex justify-content-between align-items-center">

                {remainingSeconds === 0 && !showSendAgain && !errorMessage && (
                  <span className="small-text text-nowrap text-danger">
                    Code expired
                  </span>
                )}
                {!errorMessage && !showSendAgain && remainingSeconds > 0 && (
                  <span className="small-text text-right timer w-100">
                    {formatTime(remainingSeconds)}
                  </span>
                )}
                {showSendAgain && (
                  <button
                    type="button"
                    className="small-text text-right timer link-style w-100 dark-green-color"
                    onClick={handleResendCode}
                  >
                    {formatMessage(messages['email.verification.page.send.again.link'])}
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              {failedAttempts < MAXIMUM_FAILED_ATTEMPS_RESEND_EMAIL ? (
                <>
                <div className="mb-3">
                  <p className="regular-text text-center mb-6">
                    {formatMessage(
                      failedAttempts > 0
                        ? messages['email.verification.page.code.resent.text']
                        : messages['email.verification.page.did.not.recieved.text']
                    )}
                    {' - '}
                    <button
                      type="button"
                      className="link-style dark-green-color"
                      onClick={handleResendCode}
                    >
                      {formatMessage(
                        messages['email.verification.page.send.again.link']
                      )}
                    </button>
                  </p>
                </div>
                  {resendLoading && (
                    <div className="mt-8">
                      <Lottie
                        animationData={EmailLoaderLottie}
                        style={{ width: 60, height: 60 }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <EmailRetryIssueNotice />
              )}
            </>
          )}
        </div>
        <div className="page-button-email-verification mt-5">
          <StatefulButton
            id="enter-verification-code"
            name="enter-verification-code"
            type="button"
            className="logistration-btn"
            state={isVerifying ? 'pending' : 'default'}
            labels={{
              default: showCodeInput
                ? formatMessage(messages['confirm.email.verification.code.page.button.text'])
                : formatMessage(messages['email.verification.page.button.text']),
              pending: '',
            }}
            onClick={showCodeInput ? handleVerifyCode : handleEnterCode}
            disabled={showCodeInput && (verificationCode.length < OTP_LENGTH || isVerifying)}
          />
          <ArrowButton className="page-button-email-verification-single-back" onClick={handleGoToPrevStep} />
        </div>
      </div>
    </div>
  );
};

EmailVerification.propTypes = {
  formFields: PropTypes.shape({ email: PropTypes.string }).isRequired,
  handleGoToNextStep: PropTypes.func.isRequired,
  handleGoToPrevStep: PropTypes.func,
  initialToken: PropTypes.string,
};

EmailVerification.defaultProps = {
  handleGoToPrevStep: null,
  initialToken: null,
};

export default EmailVerification;
